import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../supabase.service';
import { VoiceCommandResponse, SessionPayload } from '../models';

/**
 * Délai avant l'unique tentative de retry (MOKA-208 P5). Assez court pour
 * rester imperceptible dans une conversation, assez long pour laisser passer
 * un incident réseau/relais transitoire.
 */
const RETRY_DELAY_MS = 500;

/** Corps JSON éventuellement renvoyé par l'edge function sur un échec HTTP. */
interface VoiceCommandErrorBody {
  error?: string;
  message?: string;
}

/** Résultat interne d'une tentative d'appel — distingue succès, échec retryable et échec terminal. */
interface InvokeAttempt {
  result: VoiceCommandResponse;
  /** `true` uniquement pour un échec transitoire (réseau, relais, 5xx) qu'un second essai peut résoudre. */
  retryable: boolean;
}

/**
 * Service pour communiquer avec les Edge Functions vocales
 */
@Injectable({ providedIn: 'root' })
export class VoiceApiService {
  private supabase = inject(SupabaseService);

  /**
   * Envoie l'audio à l'Edge Function pour transcription Whisper
   * @param audioBlob - Le blob audio enregistré
   * @param context - Module/projet courant
   * @param session - Contexte conversationnel (entités, follow-up, historique)
   * @returns Promise<VoiceCommandResponse> - Transcription et intent
   */
  async transcribe(
    audioBlob: Blob,
    context?: { module: string; projectName?: string },
    session?: SessionPayload
  ): Promise<VoiceCommandResponse> {
    try {
      // Convertir le blob en base64 pour l'envoi
      const base64Audio = await this.blobToBase64(audioBlob);

      const body: Record<string, unknown> = {
        audio: base64Audio,
        mimeType: audioBlob.type
      };

      if (context) {
        body['context'] = context;
      }

      if (session) {
        body['session'] = session;
      }

      return await this.#invokeVoiceCommand(body, 'Erreur lors de la transcription');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      return {
        success: false,
        transcript: '',
        intent: null,
        error: message
      };
    }
  }

  /**
   * Envoie une commande texte à l'Edge Function (saisie écrite, bypass Whisper)
   * @param text - Le texte saisi par l'utilisateur
   * @param context - Module/projet courant
   * @param session - Contexte conversationnel (entités, follow-up, historique)
   * @returns Promise<VoiceCommandResponse> - Transcript (= text) et intent
   */
  async sendText(
    text: string,
    context?: { module: string; projectName?: string },
    session?: SessionPayload
  ): Promise<VoiceCommandResponse> {
    try {
      const body: Record<string, unknown> = { text };

      if (context) {
        body['context'] = context;
      }

      if (session) {
        body['session'] = session;
      }

      return await this.#invokeVoiceCommand(body, 'Erreur lors du traitement de la commande');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      return {
        success: false,
        transcript: '',
        intent: null,
        error: message
      };
    }
  }

  /**
   * Point d'entrée unique de l'appel `voice-command`, partagé par `transcribe()`
   * et `sendText()` (MOKA-208 P5) — seule la construction du `body` diffère
   * entre les deux, toute la classification d'erreur et le retry vivent ici.
   *
   * GARANTIE DE SÉCURITÉ contre les doubles exécutions : ce retry ne rejoue
   * QUE cet appel HTTP `voice-command` (transcription Whisper + parsing/
   * classification GPT côté edge function). Il s'exécute intégralement dans
   * `VoiceApiService`, AVANT tout `VoiceIntent` exploitable et bien avant tout
   * appel à `ServiceRouterService.execute()`/`VoiceService.executeIntent()` —
   * ces derniers vivent dans `VoiceService` et ne sont invoqués qu'après
   * qu'un `VoiceCommandResponse.success === true` a été retourné par CETTE
   * méthode. Rejouer l'appel une seconde fois ici ne peut donc jamais
   * déclencher deux fois un handler métier (création de tâche, suppression,
   * assignation…) : au pire, GPT est interrogé deux fois pour la même
   * phrase, ce qui n'a aucun effet de bord côté données.
   */
  async #invokeVoiceCommand(
    body: Record<string, unknown>,
    genericErrorMessage: string
  ): Promise<VoiceCommandResponse> {
    const first = await this.#invokeOnce(body, genericErrorMessage);
    if (!first.retryable) {
      return first.result;
    }

    await new Promise<void>(resolve => setTimeout(resolve, RETRY_DELAY_MS));
    const second = await this.#invokeOnce(body, genericErrorMessage);
    return second.result;
  }

  /**
   * Une tentative d'appel `voice-command`, avec classification de l'erreur
   * éventuelle selon `error.name` (cf. `@supabase/functions-js`) :
   * - `FunctionsFetchError` : échec réseau pur, aucune `response` — retryable.
   * - `FunctionsRelayError` : erreur de relais Supabase, `response` présente
   *   mais généralement sans JSON exploitable — retryable.
   * - `FunctionsHttpError` : l'edge function a répondu un statut non-2xx,
   *   `response` est la Response HTTP brute (pas encore consommée) : on tente
   *   d'y lire un corps JSON. Un 429 (ou un corps `{ error: 'quota_exceeded' }`)
   *   est un dépassement de quota — jamais retryable, jamais traité comme le
   *   mode dégradé générique. Les autres 4xx (400/401/403/404…) ne sont pas
   *   retryables non plus : une demande mal formée ne devient pas valide en
   *   la rejouant. Seuls les 5xx le sont.
   */
  async #invokeOnce(
    body: Record<string, unknown>,
    genericErrorMessage: string
  ): Promise<InvokeAttempt> {
    const { data, error, response } = await this.supabase.client.functions.invoke(
      'voice-command',
      { body }
    );

    if (!error) {
      return { retryable: false, result: data as VoiceCommandResponse };
    }

    if (error.name === 'FunctionsHttpError' && response) {
      const jsonBody = (await response.json().catch(() => null)) as VoiceCommandErrorBody | null;

      if (response.status === 429 || jsonBody?.error === 'quota_exceeded') {
        return {
          retryable: false,
          result: {
            success: false,
            transcript: '',
            intent: null,
            error: jsonBody?.message || 'Quota de requêtes de l\'assistant atteint.',
            errorCode: 'quota_exceeded'
          }
        };
      }

      return {
        retryable: response.status >= 500,
        result: {
          success: false,
          transcript: '',
          intent: null,
          error: jsonBody?.message || error.message || genericErrorMessage
        }
      };
    }

    // FunctionsFetchError (pas de `response`) ou FunctionsRelayError
    // (`response` présente mais sans JSON exploitable) : toutes deux retryables.
    const retryable = error.name === 'FunctionsFetchError' || error.name === 'FunctionsRelayError';
    return {
      retryable,
      result: {
        success: false,
        transcript: '',
        intent: null,
        error: error.message || genericErrorMessage
      }
    };
  }

  /**
   * Demande la synthèse vocale via l'Edge Function TTS (audio WAV, MOKA-337).
   * Passe par `functions.invoke` pour envoyer le JWT de la session : la fonction contrôle
   * l'abonnement de l'organisation de l'appelant, la clé anonyme y était refusée (401).
   */
  async textToSpeech(text: string): Promise<Blob> {
    const { data, error } = await this.supabase.client.functions.invoke<Blob>('voice-tts', { body: { text } });
    if (error || !(data instanceof Blob)) {
      throw new Error(error?.message || 'Erreur TTS');
    }
    return data;
  }

  /**
   * Convertit un Blob en string base64
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Retirer le préfixe data:audio/...;base64,
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
